/**
 * Pokemon Showdown Dex
 *
 * Roughly equivalent to sim/dex.js in a Pokemon Showdown server, but
 * designed for use in browsers rather than in Node.
 *
 * This is a generic utility library for Pokemon Showdown code: any
 * code shared between the replay viewer and the client usually ends up
 * here.
 *
 * Licensing note: PS's client has complicated licensing:
 * - The client as a whole is AGPLv3
 * - The battle replay/animation engine (battle-*.ts) by itself is MIT
 *
 * Compiled into battledata.js which includes all dependencies
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license MIT
 */

import { Pokemon, type ServerPokemon } from "./battle";
import {
	BattleAvatarNumbers, BattleBaseSpeciesChart, BattlePokemonIconIndexes, BattlePokemonIconIndexesLeft,
	Ability, Item, Move, Species, PureEffect, type ID, type Type,
} from "./battle-dex-data";
import type * as DexData from "./battle-dex-data";
import type { Teams } from "./battle-teams";
import { Config } from "./client-main";

export declare namespace Dex {
	/* eslint-disable @typescript-eslint/no-shadow */
	export type Ability = DexData.Ability;
	export type Item = DexData.Item;
	export type Move = DexData.Move;
	export type Species = DexData.Species;
	export type Type = DexData.Type;
	export type Nature = DexData.Nature;
	export type PureEffect = DexData.PureEffect;
	export type Effect = DexData.Effect;
	export type ID = DexData.ID;
	/* eslint-enable @typescript-eslint/no-shadow */
	export type StatName = DexData.StatName;
	export type StatNameExceptHP = DexData.StatNameExceptHP;
	export type BoostStatName = DexData.BoostStatName;
	export type TypeName = DexData.TypeName;
	export type CategoryName = DexData.CategoryName;
	export type StatusName = DexData.StatusName;
	export type GenderName = DexData.GenderName;
	export type NatureName = DexData.NatureName;
	export type MoveTarget = DexData.MoveTarget;
	export type REGULAR = 0;
	export type WEAK = 1;
	export type RESIST = 2;
	export type IMMUNE = 3;
	export type WeaknessType = REGULAR | WEAK | RESIST | IMMUNE;
	export type StatsTable = { hp: number, atk: number, def: number, spa: number, spd: number, spe: number };
	export type PokemonSet = Teams.PokemonSet;
}
export type { ID };

declare const require: any;
declare const global: any;
declare const process: any;

if (typeof window === 'undefined') {
	// Node
	global.window = global;
} else {
	// browser (possibly NW.js!)
	window.exports = window;
}

window.nodewebkit = !!(typeof process !== 'undefined' && process.versions?.['node-webkit']);

export function toID(text: any) {
	if (text?.id) {
		text = text.id;
	} else if (text?.userid) {
		text = text.userid;
	}
	if (typeof text !== 'string' && typeof text !== 'number') return '' as ID;
	return `${text}`.toLowerCase().replace(/[^a-z0-9]+/g, '') as ID;
}

export function toUserid(text: any) {
	return toID(text);
}

const TEXT_LANGUAGES: { [language: string]: string } = {
	english: 'en', german: 'de', spanish: 'es', french: 'fr', italian: 'it',
	japanese: 'ja', korean: 'ko', simplifiedchinese: 'zh-cn', traditionalchinese: 'zh-tw',
};

interface ClientDexText {
	getLanguage(): string;
	get(effect: Species | Item | Ability | Move, lang?: string): BattleTextEntry;
}

function getTextLanguage() {
	const preference = Dex.prefs('language') || Dex.prefs('serversettings')?.language || 'english';
	const language = TEXT_LANGUAGES[preference] || 'en';
	return language === 'en' && Dex.afdMode === true ? 'en-afd' : language;
}

function getTextEntry(effect: Species | Item | Ability | Move, gen: number, lang: string): BattleTextEntry {
	const tableName = `${effect.effectType === 'Species' ? 'Pokedex' : `${effect.effectType}s`}` as keyof BattleTextData;
	const english = BattleText.en?.[tableName]?.[effect.id] || {};
	const localized = BattleText[lang]?.[tableName]?.[effect.id] || {};
	const entry: BattleTextEntry = { ...english, ...localized };
	for (let i = 1; i <= 8; i++) {
		const genName = `gen${i}`;
		const englishGen = english[genName];
		const localizedGen = localized[genName];
		if (typeof englishGen === 'object' || typeof localizedGen === 'object') {
			entry[genName] = {
				...(typeof englishGen === 'object' ? englishGen : {}),
				...(typeof localizedGen === 'object' ? localizedGen : {}),
			};
		}
	}
	for (let i = 8; i >= gen; i--) {
		const genName = `gen${i}`;
		Object.assign(entry, english[genName] || {}, localized[genName] || {});
	}
	return entry;
}

type Comparable = number | string | boolean | Comparable[] | { reverse: Comparable };
export const PSUtils = new class {
	/**
	 * Like string.split(delimiter), but only recognizes the first `limit`
	 * delimiters (default 1).
	 *
	 * `"1 2 3 4".split(" ", 2) => ["1", "2"]`
	 *
	 * `splitFirst("1 2 3 4", " ", 1) => ["1", "2 3 4"]`
	 *
	 * Returns an array of length exactly limit + 1.
	 */
	splitFirst(str: string, delimiter: string, limit = 1) {
		let splitStr: string[] = [];
		while (splitStr.length < limit) {
			let delimiterIndex = str.indexOf(delimiter);
			if (delimiterIndex >= 0) {
				splitStr.push(str.slice(0, delimiterIndex));
				str = str.slice(delimiterIndex + delimiter.length);
			} else {
				splitStr.push(str);
				str = '';
			}
		}
		splitStr.push(str);
		return splitStr;
	}

	/**
	 * Compares two variables; intended to be used as a smarter comparator.
	 * The two variables must be the same type (TypeScript will not check this).
	 *
	 * - Numbers are sorted low-to-high, use `-val` to reverse
	 * - Strings are sorted A to Z case-semi-insensitively, use `{reverse: val}` to reverse
	 * - Booleans are sorted true-first (REVERSE of casting to numbers), use `!val` to reverse
	 * - Arrays are sorted lexically in the order of their elements
	 *
	 * In other words: `[num, str]` will be sorted A to Z, `[num, {reverse: str}]` will be sorted Z to A.
	 */
	compare(a: Comparable, b: Comparable): number {
		if (typeof a === 'number') {
			return a - (b as number);
		}
		if (typeof a === 'string') {
			return a.localeCompare(b as string);
		}
		if (typeof a === 'boolean') {
			return (a ? 1 : 2) - (b ? 1 : 2);
		}
		if (Array.isArray(a)) {
			for (let i = 0; i < a.length; i++) {
				const comparison = PSUtils.compare(a[i], (b as Comparable[])[i]);
				if (comparison) return comparison;
			}
			return 0;
		}
		if (a.reverse) {
			return PSUtils.compare((b as { reverse: string }).reverse, a.reverse);
		}
		throw new Error(`Passed value ${a as any} is not comparable`);
	}
	/**
	 * Sorts an array according to the callback's output on its elements.
	 *
	 * The callback's output is compared according to `PSUtils.compare` (in
	 * particular, it supports arrays so you can sort by multiple things).
	 */
	sortBy<T>(array: T[], callback: (a: T) => Comparable): T[];
	/**
	 * Sorts an array according to `PSUtils.compare`. (Correctly sorts numbers,
	 * unlike `array.sort`)
	 */
	sortBy<T extends Comparable>(array: T[]): T[];
	sortBy<T>(array: T[], callback?: (a: T) => Comparable) {
		if (!callback) return (array as any[]).sort(PSUtils.compare);
		return array.sort((a, b) => PSUtils.compare(callback(a), callback(b)));
	}
	normalizeError(err: any): string {
		const stack = err.stack || '';
		const messagePrefix = `${err.name}: ${err.message}`;

		// Firefox doesn't put the error message inside the stack trace,
		// but Chrome/Safari does
		if (stack && !stack.startsWith(messagePrefix)) {
			return `${messagePrefix}\n${stack}`;
		}
		return stack || messagePrefix;
	}
};

/**
 * Sanitize a room ID by removing anything that isn't alphanumeric or `-`.
 * Shouldn't actually do anything except against malicious input.
 */
export function toRoomid(roomid: string) {
	return roomid.replace(/[^a-zA-Z0-9-]+/g, '').toLowerCase();
}

export function toName(name: any) {
	if (typeof name !== 'string' && typeof name !== 'number') return '';
	name = `${name}`.replace(/[|\s[\],\u202e]+/g, ' ').trim();
	if (name.length > 18) name = name.substr(0, 18).trim();

	// remove zalgo
	name = name.replace(
		/[\u0300-\u036f\u0483-\u0489\u0610-\u0615\u064B-\u065F\u0670\u06D6-\u06DC\u06DF-\u06ED\u0E31\u0E34-\u0E3A\u0E47-\u0E4E]{3,}/g,
		''
	);
	name = name.replace(/[\u239b-\u23b9]/g, '');

	return name;
}

export interface SpriteData {
	w: number;
	h: number;
	y?: number;
	gen?: number;
	url?: string;
	rawHTML?: string;
	pixelated?: boolean;
	isFrontSprite?: boolean;
	cryurl?: string;
	shiny?: boolean;
}

export interface TeambuilderSpriteData {
	x: number;
	y: number;
	h?: number;
	spriteDir?: string;
	spriteid?: string;
	shiny?: boolean;
	url?: string;
	pixelated?: boolean;
}

export const Dex = new class implements ModdedDex {
	readonly Ability = Ability;
	readonly Item = Item;
	readonly Move = Move;
	readonly Species = Species;

	readonly gen = 9;
	readonly modid = 'gen9' as ID;
	readonly cache = null!;

	readonly REGULAR = 0;
	readonly WEAK = 1;
	readonly RESIST = 2;
	readonly IMMUNE = 3;

	readonly statNames: readonly Dex.StatName[] = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];
	readonly statNamesExceptHP: readonly Dex.StatNameExceptHP[] = ['atk', 'def', 'spa', 'spd', 'spe'];

	pokeballs: string[] | null = null;

	resourcePrefix = (() => {
		if (window.location.protocol === 'file:') return 'https://pcr-showdown.com/';
		if (window.Config?.routes?.client) return `//${Config.routes.client}/`;
		return `${window.location.origin}/`;
	})();

	fxPrefix = (() => {
		return 'https://play.pokemonshowdown.com/fx/';
	})();

	loadedSpriteData = { xy: 1, bw: 0 };
	loadedTextData: { [lang: string]: 1 | Promise<void> } = { en: 1 };
	moddedDexes: { [mod: string]: ModdedDex } = {};

	/**
	 * April Fools' Day setting:
	 * * `true` = FULL, all jokes on
	 * * `'sprites'` = SPRITES, only sprites and taunts
	 * * `false | null | undefined` = OFF
	 */
	afdMode?: boolean | 'sprites';

	mod(modid: ID): ModdedDex {
		if (modid === 'gen9') return this;
		if (!window.BattleTeambuilderTable) return this;
		if (modid in this.moddedDexes) {
			return this.moddedDexes[modid];
		}
		this.moddedDexes[modid] = new ModdedDex(modid);
		return this.moddedDexes[modid];
	}
	forGen(gen: number) {
		if (!gen) return this;
		return this.mod(`gen${gen}` as ID);
	}
	formatGen(format: string) {
		const formatid = toID(format);
		if (!formatid) return Dex.gen;
		if (!formatid.startsWith('gen')) return 6;
		return parseInt(formatid.charAt(3)) || Dex.gen;
	}
	forFormat(format: string) {
		let dex = Dex.forGen(Dex.formatGen(format));

		const formatid = toID(format).slice(4);
		if (dex.gen === 7 && formatid.includes('letsgo')) {
			dex = Dex.mod('gen7letsgo' as ID);
		}
		if (dex.gen === 8 && formatid.includes('bdsp')) {
			dex = Dex.mod('gen8bdsp' as ID);
		}
		if (dex.gen === 9 && formatid.includes('champions')) {
			dex = Dex.mod('champions' as ID);
		}
		if (dex.gen === 9 && formatid.includes('rejuvenation')) {
			dex = Dex.mod('gen9rejuvenation' as ID);
		}

		return dex;
	}

	resolveAvatar(avatar: string): string {
		const originalAvatar = avatar;

		// Old/default Showdown avatars arrive as numbers.
		if (window.BattleAvatarNumbers && avatar in BattleAvatarNumbers) {
			avatar = BattleAvatarNumbers[avatar];
		}

		// Replace numeric defaults with an avatar that actually exists locally.
		if (/^\d+$/.test(originalAvatar)) {
			avatar = 'melia - rejuvenation';
		}

		if (avatar.startsWith('#')) {
			return (
				Dex.resourcePrefix +
				'sprites/trainers-custom/' +
				toID(avatar.slice(1)) +
				'.png'
			);
		}

		if (avatar.includes('.') && window.Config?.server?.registered) {
			const protocol = Config.server.port === 443 ? 'https' : 'http';
			const server =
				`${protocol}://${Config.server.host}:${Config.server.port}`;

			return (
				`${server}/avatars/` +
				encodeURIComponent(avatar).replace(/%3F/g, '?')
			);
		}

		return (
			Dex.resourcePrefix +
			'sprites/trainers/' +
			encodeURIComponent(avatar || 'aero - rejuvenation') +
			'.png'
		);
	}

	/**
	 * This is used to sanitize strings from data files like `moves.js` and
	 * `teambuilder-tables.js`.
	 *
	 * This makes sure untrusted strings can't wreak havoc if someone forgets to
	 * escape it before putting it in HTML.
	 *
	 * None of these characters belong in these files, anyway. (They can be used
	 * in move descriptions, but those are served from `text.js`, which are
	 * definitely always treated as unsanitized.)
	 */
	sanitizeName(name: any) {
		if (!name) return '';
		return ('' + name)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
			.slice(0, 50);
	}

	prefs(prop: string) {
		// @ts-expect-error this is what I get for calling it Storage...
		return window.Storage?.prefs ? window.Storage.prefs(prop) : window.PS?.prefs?.[prop];
	}

	text: ClientDexText = {
		getLanguage: getTextLanguage,
		get: (effect: Species | Item | Ability | Move, lang = getTextLanguage()) => {
			return getTextEntry(effect, 9, lang);
		},
	};

	getShortName(name: string) {
		let shortName = name.replace(/[^A-Za-z0-9]+$/, '');
		if (shortName.includes('(')) {
			shortName += name.slice(shortName.length).replace(/[^()]+/g, '').replace(/\(\)/g, '');
		}
		return shortName;
	}

	getEffect(name: string | null | undefined): PureEffect | Item | Ability | Move {
		name = (name || '').trim();
		if (name.substr(0, 5) === 'item:') {
			return Dex.items.get(name.substr(5).trim());
		} else if (name.substr(0, 8) === 'ability:') {
			return Dex.abilities.get(name.substr(8).trim());
		} else if (name.substr(0, 5) === 'move:') {
			return Dex.moves.get(name.substr(5).trim());
		}
		let id = toID(name);
		return new PureEffect(id, name);
	}

	moves = {
		get: (nameOrMove: string | Move | null | undefined): Move => {
			if (nameOrMove && typeof nameOrMove !== 'string') {
				// TODO: don't accept Moves here
				return nameOrMove;
			}
			let name = nameOrMove || '';
			let id = toID(nameOrMove);
			if (window.BattleAliases && id in BattleAliases) {
				name = BattleAliases[id];
				id = toID(name);
			}
			if (!window.BattleMovedex) window.BattleMovedex = {};
			let data = window.BattleMovedex[id];
			if (data && typeof data.exists === 'boolean') return data;

			if (!data && id.substr(0, 11) === 'hiddenpower' && id.length > 11) {
				let [, hpWithType, hpPower] = /([a-z]*)([0-9]*)/.exec(id)!;
				data = {
					...(window.BattleMovedex[hpWithType] || {}),
					basePower: Number(hpPower) || 60,
				};
			}
			if (!data && id.substr(0, 6) === 'return' && id.length > 6) {
				data = {
					...(window.BattleMovedex['return'] || {}),
					basePower: Number(id.slice(6)),
				};
			}
			if (!data && id.substr(0, 11) === 'frustration' && id.length > 11) {
				data = {
					...(window.BattleMovedex['frustration'] || {}),
					basePower: Number(id.slice(11)),
				};
			}

			if (!data) data = { exists: false };
			let move = new Move(id, name, data);
			window.BattleMovedex[id] = move;
			return move;
		},
	};

	getGen3Category(type: string) {
		return [
			'Fire', 'Water', 'Grass', 'Electric', 'Ice', 'Psychic', 'Dark', 'Dragon',
		].includes(type) ? 'Special' : 'Physical';
	}

	items = {
		get: (nameOrItem: string | Item | null | undefined): Item => {
			if (nameOrItem && typeof nameOrItem !== 'string') {
				// TODO: don't accept Items here
				return nameOrItem;
			}
			let name = nameOrItem || '';
			let id = toID(nameOrItem);
			if (window.BattleAliases && id in BattleAliases) {
				name = BattleAliases[id];
				id = toID(name);
			}
			if (!window.BattleItems) window.BattleItems = {};
			let data = window.BattleItems[id];
			if (data && typeof data.exists === 'boolean') return data;
			if (!data) data = { exists: false };
			let item = new Item(id, name, data);
			window.BattleItems[id] = item;
			return item;
		},
	};

	abilities = {
		get: (nameOrAbility: string | Ability | null | undefined): Ability => {
			if (nameOrAbility && typeof nameOrAbility !== 'string') {
				// TODO: don't accept Abilities here
				return nameOrAbility;
			}
			let name = nameOrAbility || '';
			let id = toID(nameOrAbility);
			if (window.BattleAliases && id in BattleAliases) {
				name = BattleAliases[id];
				id = toID(name);
			}
			if (!window.BattleAbilities) window.BattleAbilities = {};
			let data = window.BattleAbilities[id];
			if (data && typeof data.exists === 'boolean') return data;
			if (!data) data = { exists: false };
			let ability = new Ability(id, name, data);
			window.BattleAbilities[id] = ability;
			return ability;
		},
	};

	species = {
		get: (nameOrSpecies: string | Species | null | undefined): Species => {
			if (nameOrSpecies && typeof nameOrSpecies !== 'string') {
				// TODO: don't accept Species' here
				return nameOrSpecies;
			}
			let name = nameOrSpecies || '';
			let id = toID(nameOrSpecies);
			let formid = id;
			if (!window.BattlePokedexAltForms) window.BattlePokedexAltForms = {};
			if (formid in window.BattlePokedexAltForms) return window.BattlePokedexAltForms[formid];
			if (window.BattleAliases && id in BattleAliases) {
				name = BattleAliases[id];
				id = toID(name);
			} else if (window.BattlePokedex && !(id in BattlePokedex) && window.BattleBaseSpeciesChart) {
				for (const baseSpeciesId of BattleBaseSpeciesChart) {
					if (formid.startsWith(baseSpeciesId)) {
						id = baseSpeciesId;
						break;
					}
				}
			}
			if (!window.BattlePokedex) window.BattlePokedex = {};
			let data = window.BattlePokedex[id];

			let species: Species;
			if (data && typeof data.exists === 'boolean') {
				species = data;
			} else {
				if (!data) data = { exists: false };
				if (!data.tier && id.endsWith('totem')) {
					data.tier = this.species.get(id.slice(0, -5)).tier;
				}
				if (!data.tier && data.baseSpecies && toID(data.baseSpecies) !== id) {
					data.tier = this.species.get(data.baseSpecies).tier;
				}
				data.nfe = data.id === 'dipplin' || !!(data as Species).evos?.some(evo => {
					const evoSpecies = this.species.get(evo);
					return !evoSpecies.isNonstandard || evoSpecies.isNonstandard === data.isNonstandard ||
						// Pokemon with Hisui evolutions
						evoSpecies.isNonstandard === "Unobtainable";
				});
				species = new Species(id, name, data);
				window.BattlePokedex[id] = species;
			}

			if (species.cosmeticFormes) {
				for (const forme of species.cosmeticFormes) {
					if (toID(forme) === formid) {
						species = new Species(formid, name, {
							...species,
							name: forme,
							forme: forme.slice(species.name.length + 1),
							baseForme: "",
							baseSpecies: species.name,
							otherFormes: null,
						});
						window.BattlePokedexAltForms[formid] = species;
						break;
					}
				}
			}

			return species;
		},
	};

	toDexNumber(pokemon: Pokemon | Species | string | null | undefined) {
		if (!pokemon) return 0;
		if (pokemon instanceof Pokemon) {
			return this.species.get(pokemon.getSpeciesForme()).num;
		}
		if (typeof pokemon === 'string') {
			return this.species.get(pokemon).num;
		}
		return pokemon.num;
	}

	formNumber(pokemon: Pokemon | Species | string | null | undefined, dex: ModdedDex = this) {
		if (!pokemon) return 0;
		let species: Species;
		if (pokemon instanceof Pokemon) {
			species = dex.species.get(pokemon.getSpeciesForme());
		} else if (typeof pokemon === 'string') {
			species = dex.species.get(pokemon);
		} else {
			species = dex.species.get(pokemon.name);
		}
		if (dex.modid.includes('rejuvenation')) {
			const florgesFormes: Record<string, number> = {
				florges: 0,
				florgesblue: 1,
				florgesblack: 2,
				florgesgreen: 3,
				florgespurple: 4,
				florgesrebornflower: 5,
			};
			if (species.id in florgesFormes) return florgesFormes[species.id];
		}
		const baseSpecies = dex.species.get(
			species.baseSpecies || species.name
		);
		const formeOrder = baseSpecies.formeOrder || [
			baseSpecies.name,
			...(baseSpecies.cosmeticFormes || []),
			...(baseSpecies.otherFormes || []),
		];
		const formeNumber = formeOrder.indexOf(species.name);
		return formeNumber >= 0 ? formeNumber : 0;
	}

	hasFemaleIcon(pokemon: Pokemon | Species | string | null | undefined, femaleIconNumbers: number[]) {
		if (!pokemon) return false;

		let species: Species;

		if (pokemon instanceof Pokemon) {
			species = this.species.get(pokemon.getSpeciesForme());
		} else if (typeof pokemon === 'string') {
			species = this.species.get(pokemon);
		} else {
			species = this.species.get(pokemon.name);
		}

		return femaleIconNumbers.includes(species.num);
	}

	types = {
		allCache: null as Type[] | null,
		namesCache: null as Dex.TypeName[] | null,
		get: (type: any): Type => {
			if (!type || typeof type === 'string') {
				const id = toID(type) as string;
				const name = id.substr(0, 1).toUpperCase() + id.substr(1);
				type = window.BattleTypeChart?.[id] || {};
				if (type.damageTaken) type.exists = true;
				if (!type.id) type.id = id;
				if (!type.name) type.name = name;
				if (!type.effectType) {
					type.effectType = 'Type';
				}
			}
			return type;
		},
		all: (): readonly Type[] => {
			if (this.types.allCache) return this.types.allCache;
			const types = [];
			for (const id in (window.BattleTypeChart || {})) {
				types.push(Dex.types.get(id));
			}
			if (types.length) this.types.allCache = types;
			return types;
		},
		names: (): readonly Dex.TypeName[] => {
			if (this.types.namesCache) return this.types.namesCache;
			const names = this.types.all().map(type => type.name as Dex.TypeName);
			names.splice(names.indexOf('Stellar'), 1);
			if (names.length) this.types.namesCache = names;
			return names;
		},
		isName: (name: string | null): boolean => {
			const id = toID(name);
			if (name !== id.substr(0, 1).toUpperCase() + id.substr(1)) return false;
			return window.BattleTypeChart?.hasOwnProperty(id);
		},
	};

	hasAbility(species: Species, ability: string) {
		for (const i in species.abilities) {
			if (ability === species.abilities[i as '0']) return true;
		}
		return false;
	}

	loadSpriteData(gen: 'xy' | 'bw') {
		if (this.loadedSpriteData[gen]) return;
		this.loadedSpriteData[gen] = 1;

		let path = $('script[src*="pokedex-mini.js"]').attr('src') || '';
		let qs = '?' + (path.split('?')[1] || '');
		path = ((/.+?(?=data\/pokedex-mini\.js)/.exec(path)) || [])[0] || '';

		let el = document.createElement('script');
		el.src = path + 'data/pokedex-mini-bw.js' + qs;
		document.getElementsByTagName('body')[0].appendChild(el);
	}
	loadTextData(lang = this.text.getLanguage()): Promise<void> {
		lang = TEXT_LANGUAGES[lang] || lang;
		if (BattleText[lang] || typeof document === 'undefined') return Promise.resolve();
		const existing = this.loadedTextData[lang];
		if (existing) return existing === 1 ? Promise.resolve() : existing;

		const loadScript = (src: string) => new Promise<void>((resolve, reject) => {
			const el = document.createElement('script');
			el.src = src;
			el.onload = () => resolve();
			el.onerror = () => reject(new Error(`Failed to load text data from ${src}`));
			document.getElementsByTagName('body')[0].appendChild(el);
		});
		let loading = loadScript(Config.testclient ? `data/text/${lang}.js` : `${this.resourcePrefix}data/text/${lang}.js`);
		if (Config.testclient) {
			loading = loading.catch(() => loadScript(`https://play.pokemonshowdown.com/data/text/${lang}.js`));
		}
		loading = loading.catch(() => {
			delete this.loadedTextData[lang];
		});
		this.loadedTextData[lang] = loading;
		return loading;
	}
	getSpriteData(pokemon: Pokemon | Species | string, isFront: boolean, options: {
		gen?: number,
		shiny?: boolean,
		gender?: Dex.GenderName,
		afd?: boolean,
		noScale?: boolean,
		mod?: string,
		dynamax?: boolean,
	} = { gen: 6 }) {
		const mechanicsGen = options.gen || 6;
		let isDynamax = !!options.dynamax;
		if (pokemon instanceof Pokemon) {
			if (pokemon.volatiles.transform) {
				options.shiny = pokemon.volatiles.transform[2];
				options.gender = pokemon.volatiles.transform[3];
			} else {
				options.shiny = pokemon.shiny;
				options.gender = pokemon.gender;
			}
			let isGigantamax = false;
			if (pokemon.volatiles.dynamax) {
				if (pokemon.volatiles.dynamax[1]) {
					isGigantamax = true;
				} else if (options.dynamax !== false) {
					isDynamax = true;
				}
			}
			pokemon = pokemon.getSpeciesForme() + (isGigantamax ? '-Gmax' : '');
		}
		const spriteDex = options.mod?.includes('rejuvenation') ? Dex.mod('gen9rejuvenation' as ID) : Dex;
		const species = spriteDex.species.get(typeof pokemon === 'string' ? pokemon : pokemon.name);
		const mod = options.mod || '';
		// Gmax sprites are already extremely large, so we don't need to double.
		if (species.name.endsWith('-Gmax')) isDynamax = false;
		let spriteData = {
			gen: mechanicsGen,
			w: 96,
			h: 96,
			y: 0,
			url: Dex.resourcePrefix + 'sprites/',
			pixelated: true,
			isFrontSprite: false,
			cryurl: '',
			shiny: options.shiny,
		};
		let name = species.spriteid;
		if (options.mod?.includes('rejuvenation') && species.id === 'florgesrebornflower') {
			name = 'florges-reborn-flower';
		}
		let dir;
		let facing;
		if (isFront) {
			spriteData.isFrontSprite = true;
			dir = '';
			facing = 'front';
		} else {
			dir = '-back';
			facing = 'back';
		}

		// Decide which gen sprites to use.
		//
		// There are several different generations we care about here:
		//
		//   - mechanicsGen: the generation number of the mechanics and battle (options.gen)
		//   - graphicsGen: the generation number of sprite/field graphics the user has requested.
		//     This will default to mechanicsGen, but may be altered depending on user preferences.
		//   - spriteData.gen: the generation number of a the specific Pokemon sprite in question.
		//     This defaults to graphicsGen, but if the graphicsGen doesn't have a sprite for the Pokemon
		//     (eg. Darmanitan in graphicsGen 2) then we go up gens until it exists.
		//
		let graphicsGen = mechanicsGen;
		if (Dex.prefs('nopastgens')) graphicsGen = 6;
		if (Dex.prefs('bwgfx') && graphicsGen >= 6) graphicsGen = 5;
		spriteData.gen = Math.max(graphicsGen, Math.min(species.gen, 5));
		const baseDir = ['', 'gen1', 'gen2', 'gen3', 'gen4', 'gen5', '', '', '', ''][spriteData.gen];

		let miscData = null;
		let speciesid = species.id;
		if (species.isTotem) speciesid = toID(name);
		if (window.BattlePokemonSprites) miscData = BattlePokemonSprites[speciesid];
		if (!miscData && window.BattlePokemonSpritesBW) miscData = BattlePokemonSpritesBW[speciesid];
		if (!miscData) miscData = {};

		if (miscData.num !== 0 && miscData.num > -5000) {
			let baseSpeciesid = toID(species.baseSpecies);
			spriteData.cryurl = 'audio/cries/' + baseSpeciesid;
			let formeid = species.formeid;
			const specialFormeCries = [
				'-bloodmoon', '-crowned', '-eternal', '-eternamax', '-four', '-hangry', '-hero', '-lowkey', '-noice', '-primal', '-rapidstrike', '-roaming', '-school', '-sky', '-starter', '-super', '-therian', '-unbound',
			];
			const specialBaseSpeciesCries = [
				'calyrex', 'kyurem', 'cramorant', 'indeedee', 'lycanroc', 'necrozma', 'oinkologne', 'oricorio', 'slowpoke', 'tatsugiri', 'zygarde',
			];
			if (species.isMega ||
				formeid && (specialFormeCries.includes(formeid) || specialBaseSpeciesCries.includes(baseSpeciesid))) {
				if (species.isMega && (baseSpeciesid === 'meowstic' || baseSpeciesid === 'tatsugiri')) {
					spriteData.cryurl += '-mega';
				} else {
					spriteData.cryurl += formeid;
				}
			}
			spriteData.cryurl += '.mp3';
		}

		if (options.shiny && mechanicsGen > 1) dir += '-shiny';

		// April Fool's 2014
		if (Dex.afdMode || options.afd) {
			// Explicit false check above means AFD will be off if the user disables it - no matter what
			dir = 'afd' + dir;
			spriteData.url += dir + '/' + name + '.png';
			// Duplicate code but needed to make AFD tinymax work
			// April Fool's 2020
			if (isDynamax && !options.noScale) {
				spriteData.w *= 0.25;
				spriteData.h *= 0.25;
				spriteData.y += -22;
			} else if (species.isTotem && !options.noScale) {
				spriteData.w *= 0.5;
				spriteData.h *= 0.5;
				spriteData.y += -11;
			}
			return spriteData;
		}

		// Mod Cries
		if (options.mod) {
			spriteData.cryurl = `sprites/${options.mod}/audio/${toID(species.baseSpecies)}`;
			spriteData.cryurl += '.mp3';
		}

		let animatedSprite = false;
		if (!Dex.prefs('noanim') && !Dex.prefs('nogif') && spriteData.gen >= 5) {

			// New
			if (options.mod) {
				if (options.mod !== 'rejuvenation') {
					let modDir = options.mod;
					if (!spriteData.isFrontSprite) modDir += '-back';
					if (options.shiny) modDir += '-shiny';
					dir = `${modDir}/ani`;
					spriteData.url = `${this.resourcePrefix}sprites/${dir}/${name}.gif`;
					spriteData.w = 96;
					spriteData.h = 96;
					animatedSprite = true;
				}
			} else {
				const animationArray: [AnyObject, string][] = [];
				if (baseDir === '' && window.BattlePokemonSprites) {
					animationArray.push([BattlePokemonSprites[speciesid], '']);
				}
				if (window.BattlePokemonSpritesBW) {
					animationArray.push([BattlePokemonSpritesBW[speciesid], 'gen5']);
				}
				for (const [animationData, animDir] of animationArray) {
					if (!animationData) continue;
					if (animationData[facing + 'f'] && options.gender === 'F') facing += 'f';
					if (!animationData[facing]) continue;
					if (facing.endsWith('f')) name += '-f';
					if (spriteData.gen >= 6) spriteData.pixelated = false;
					if (options.mod) {
						dir = `${options.mod}/ani` + dir;
					} 
					else {
						dir = animDir + 'ani' + dir;
					}
					spriteData.w = animationData[facing].w;
					spriteData.h = animationData[facing].h;
					spriteData.url += dir + '/' + name + '.gif';
					animatedSprite = true;
					break;
				}
			}
		}
		if (!animatedSprite) {
			if (options.mod) {
				if (options.mod === 'rejuvenation') {
					let battlerDir: string;
					if (spriteData.isFrontSprite) {
						battlerDir = options.shiny ? 'front-shiny' : 'front';
					} else {
						battlerDir = options.shiny ? 'back-shiny' : 'back';
					}
					spriteData.url = `${this.resourcePrefix}sprites/rejuvenation/battlers/${battlerDir}/${name}.png`;
				} else {
					let modDir = options.mod;
					if (!spriteData.isFrontSprite) modDir += '-back';
					if (options.shiny) modDir += '-shiny';
					spriteData.url = `sprites/${modDir}/${name}.png`;
				}
			} else {
				dir = (baseDir || 'gen5') + dir;
				spriteData.url += dir + '/' + name + '.png';
			}
			// // There is no entry or enough data in pokedex-mini.js
			// // Handle these in case-by-case basis; either using BW sprites or matching the played gen.
			// if (options.mod) {
			// 	dir = `${options.mod}` + dir;
			// }
			// else {
			// 	dir = (baseDir || 'gen5') + dir;
			// }

			// // Gender differences don't exist prior to Gen 4,
			// // so there are no sprites for it
			// if (spriteData.gen >= 4 && miscData['frontf'] && options.gender === 'F') {
			// 	name += '-f';
			// }

			// // if (options.mod) {
			// // 	spriteData.url = `sprites/${options.mod}/${name}.png`;
			// // }
			// if (options.mod) {
			// 	spriteData.url = `${this.resourcePrefix}${dir}/${name}.png`;
			// }
			// else {
			// 	spriteData.url += dir + '/' + name + '.png';
			// }
		}

		if (!options.noScale) {
			if (graphicsGen > 4) {
				// no scaling
			} else if (spriteData.isFrontSprite) {
				spriteData.w *= 2;
				spriteData.h *= 2;
				spriteData.y += -16;
			} else {
				// old gen backsprites are multiplied by 1.5x by the 3D engine
				spriteData.w *= 2 / 1.5;
				spriteData.h *= 2 / 1.5;
				spriteData.y += -11;
			}
			if (spriteData.gen <= 2) spriteData.y += 2;
		}
		if (isDynamax && !options.noScale) {
			spriteData.w *= 2;
			spriteData.h *= 2;
			spriteData.y += -22;
		} else if (species.isTotem && !options.noScale) {
			spriteData.w *= 1.5;
			spriteData.h *= 1.5;
			spriteData.y += -11;
		}

		return spriteData;
	}

	getPokemonIconNum(id: ID, isFemale?: boolean, facingLeft?: boolean) {
		let num = 0;
		if (window.BattlePokemonSprites?.[id]?.num) {
			num = BattlePokemonSprites[id].num;
		} else if (window.BattlePokedex?.[id]?.num) {
			num = BattlePokedex[id].num;
		}
		if (num < 0) num = 0;
		if (num > 1025) num = 0;

		if (window.BattlePokemonIconIndexes?.[id]) {
			num = BattlePokemonIconIndexes[id];
		}

		if (isFemale) {
			if (['unfezant', 'frillish', 'jellicent', 'meowstic', 'pyroar'].includes(id)) {
				num = BattlePokemonIconIndexes[id + 'f'];
			}
		}
		if (facingLeft) {
			if (BattlePokemonIconIndexesLeft[id]) {
				num = BattlePokemonIconIndexesLeft[id];
			}
		}
		return num;
	}

	getPokemonIcon(pokemon: string | Pokemon | ServerPokemon | Dex.PokemonSet | null, facingLeft?: boolean, dex: ModdedDex = this): string {
		if (pokemon === 'pokeball') {
			return `background:transparent url(${Dex.resourcePrefix}sprites/pokemonicons-pokeball-sheet.png) no-repeat scroll -0px 4px`;
		} else if (pokemon === 'pokeball-statused') {
			return `background:transparent url(${Dex.resourcePrefix}sprites/pokemonicons-pokeball-sheet.png) no-repeat scroll -40px 4px`;
		} else if (pokemon === 'pokeball-fainted') {
			return `background:transparent url(${Dex.resourcePrefix}sprites/pokemonicons-pokeball-sheet.png) no-repeat scroll -80px 4px;opacity:.4;filter:contrast(0)`;
		} else if (pokemon === 'pokeball-none') {
			return `background:transparent url(${Dex.resourcePrefix}sprites/pokemonicons-pokeball-sheet.png) no-repeat scroll -80px 4px`;
		}

		let id = toID(pokemon);
		if (pokemon instanceof Pokemon) {
			dex = pokemon.side.battle.dex;
		}
		if (!pokemon || typeof pokemon === 'string') pokemon = null;
		// @ts-expect-error safe, but too lazy to cast
		if (pokemon?.speciesForme) id = toID(pokemon.speciesForme);
		// @ts-expect-error safe, but too lazy to cast
		if (pokemon?.species) id = toID(pokemon.species);
		// @ts-expect-error safe, but too lazy to cast
		if (pokemon?.volatiles?.formechange && !pokemon.volatiles.transform) {
			// @ts-expect-error safe, but too lazy to cast
			id = toID(pokemon.volatiles.formechange[1]);
		}
		let fainted = ((pokemon as Pokemon | ServerPokemon)?.fainted ?
			`;opacity:.3;filter:grayscale(100%) brightness(.5)` : ``);
		if (dex.modid.includes('rejuvenation')) {
			const dexNumber = dex.species.get(id).num;
			const formNumber = this.formNumber(dex.species.get(id), dex);
			const isShiny = !!pokemon?.shiny;

			const femaleIconNumbers = [
				25, 26, 84, 85, 118, 119, 123, 129, 130, 190,
				202, 208, 212, 214, 224, 232, 297, 315, 322, 323,
				397, 398, 399, 400, 401, 402, 407, 415, 424, 449,
				450, 456, 457, 470, 521, 543, 544, 545, 570, 571,
				592, 593, 605, 606, 636, 637, 668, 676, 678, 739,
				740, 744, 745, 876, 902, 916,
			];

			// Each forme uses two columns: normal, then shiny.
			const column = formNumber * 2 + (isShiny ? 1 : 0);
			const left = column * 32;

			const femaleIndex = femaleIconNumbers.indexOf(dexNumber);
			const useFemaleIcon =
				pokemon?.gender === 'F' &&
				femaleIndex >= 0;

			if (useFemaleIcon) {
				const top = femaleIndex * 32;

				return `background:transparent ` +
					`url(${Dex.resourcePrefix}sprites/rejuvenation/pokemonicons/female.png?v=3) ` +
					`no-repeat scroll -${left}px -${top}px${fainted};` +
					`width:40px;height:32px;padding:0 4px;box-sizing:border-box;` +
					`background-origin:content-box;background-clip:content-box`;
			}

			// These mons take too much space so they have their own sheets.
			const specificPokemon = [
				201, 479, 493, 649, 666, 669, 670,
				671, 676, 718, 773, 774, 978,
			];

			if (specificPokemon.includes(dexNumber)) {
				return `background:transparent ` +
					`url(${Dex.resourcePrefix}sprites/rejuvenation/pokemonicons/${dexNumber}.png?v=3) ` +
					`no-repeat scroll -${left}px 0px${fainted};` +
					`width:40px;height:32px;padding:0 4px;box-sizing:border-box;` +
					`background-origin:content-box;background-clip:content-box`;
			}

			// National Dex number 1 occupies zero-based row 0.
			const top = Math.max(0, dexNumber - 1) * 32;

			return `background:transparent ` +
				`url(${Dex.resourcePrefix}sprites/rejuvenation/pokemonicons/pokemonicons-sheet.png?v=3) ` +
				`no-repeat scroll -${left}px -${top}px${fainted};` +
				`width:40px;height:32px;padding:0 4px;box-sizing:border-box;` +
				`background-origin:content-box;background-clip:content-box`;
		}
		
		let num = this.getPokemonIconNum(id, pokemon?.gender === 'F', facingLeft);

		let top = Math.floor(num / 12) * 30;
		let left = (num % 12) * 40;

		return `background:transparent url(${Dex.resourcePrefix}sprites/pokemonicons-sheet.png?v22) no-repeat scroll -${left}px -${top}px${fainted}`;
	}

	getTeambuilderSpriteData(pokemon: any, dex: ModdedDex = Dex): TeambuilderSpriteData {
		let gen = dex.gen;
		let id = toID(pokemon.species || pokemon);
		let species = dex.species.get(id);
		let spriteid: string;
		if (typeof pokemon === 'string') {
			spriteid = species.spriteid || id;
		} else {
			spriteid = pokemon.spriteid;
			if (pokemon.species && !spriteid) {
				spriteid = species.spriteid || id;
			}
		}
		if (dex.modid.includes('rejuvenation') && id === 'florgesrebornflower') {
			spriteid = 'florges-reborn-flower';
		}
		
		if (dex.modid.includes('rejuvenation')) {
			const shiny = pokemon.shiny ? '-shiny' : '';

			const femaleSprites = [
				'ambipom',
				'alakazam',
				'aipom',
				'blaziken',
				'combee',
				'combusken',
				'gabite',
				'garchomp',
				'gible',
				'gligar',
				'gulpin',
				'heracross',
				'hippopotas',
				'hippowdon',
				'houndoom',
				'kadabra',
				'ledian',
				'ledyba',
				'ludicolo',
				'luxio',
				'luxray',
				'meganium',
				'octillery',
				'shinx',
				'sneasel',
				'sneaselhisui',
				'snover',
				'steelix',
				'steelixmega',
				'swalot',
				'torchic',
				'unfezant',
				'unfezantaevium',
				'venusaur',
				'weavile',
			];

			if (pokemon.gender === 'F' && femaleSprites.includes(id)) {
				spriteid += '-f';
			}

			return {
				url: `${Dex.resourcePrefix}sprites/rejuvenation/battlers/${shiny ? 'front-shiny' : 'front'}/${spriteid}.png`,
				x: 10,
				y: 5,
			};
		}
		if (species.exists === false) return { spriteDir: 'sprites/gen5', spriteid: '0', x: 10, y: 5 };
		if (Dex.afdMode) {
			return {
				spriteid,
				spriteDir: 'sprites/afd',
				shiny: !!pokemon.shiny,
				x: 10,
				y: 5,
			};
		}
		const spriteData: TeambuilderSpriteData = {
			spriteid,
			spriteDir: 'sprites/dex',
			x: -2,
			y: -3,
		};
		if (pokemon.shiny) spriteData.shiny = true;
		if (dex.modid === 'gen7letsgo') gen = 8;
		if (Dex.prefs('nopastgens')) gen = 9;
		if (Dex.prefs('bwgfx') && gen > 5) gen = 5;
		// TODO: refactor after we get home sprites for Z-A Megas and Eternal Floette
		let homeExists = (!species.isNonstandard || !['CAP', 'Custom'].includes(species.isNonstandard) ||
			species.id === "xerneasneutral") && ![
			"floetteeternal", "pichuspikyeared", "pikachubelle", "pikachucosplay", "pikachulibre", "pikachuphd", "pikachupopstar", "pikachurockstar",
		].includes(species.id) && !(species.isMega && species.gen === 9);
		if (gen >= 8 && homeExists) {
			spriteData.spriteDir = 'sprites/home-centered';
			spriteData.x = 8;
			spriteData.y = 10;
			spriteData.h = 96;
			return spriteData;
		}
		let xydexExists = (!species.isNonstandard || species.isNonstandard === 'Past' || species.isNonstandard === 'CAP') || [
			"pikachustarter", "eeveestarter", "meltan", "melmetal", "pokestarufo", "pokestarufo2", "pokestarbrycenman", "pokestarmt", "pokestarmt2", "pokestargiant", "pokestarhumanoid", "pokestarmonster", "pokestarf00", "pokestarf002", "pokestarspirit",
		].includes(species.id);
		if (species.gen >= 8 && species.isNonstandard !== 'CAP') xydexExists = false;
		if (gen >= 6 && xydexExists) {
			if (species.gen >= 7) {
				spriteData.x = -6;
				spriteData.y = -7;
			} else if (id.substr(0, 6) === 'arceus') {
				spriteData.x = -2;
				spriteData.y = 7;
			} else if (id === 'garchomp') {
				spriteData.x = -2;
				spriteData.y = 2;
			} else if (id === 'garchompmega') {
				spriteData.x = -2;
				spriteData.y = 0;
			}
			return spriteData;
		}
		spriteData.spriteDir = 'sprites/gen5';
		if (gen <= 1 && species.gen <= 1) spriteData.spriteDir = 'sprites/gen1';
		else if (gen <= 2 && species.gen <= 2) spriteData.spriteDir = 'sprites/gen2';
		else if (gen <= 3 && species.gen <= 3) spriteData.spriteDir = 'sprites/gen3';
		else if (gen <= 4 && species.gen <= 4) spriteData.spriteDir = 'sprites/gen4';
		spriteData.pixelated = true;
		spriteData.x = 10;
		spriteData.y = 5;
		return spriteData;
	}

	getTeambuilderSprite(pokemon: any, dex?: ModdedDex, xOffset = 0, yOffset = 0) {
		if (!pokemon) return '';
		const data = this.getTeambuilderSpriteData(pokemon, dex);
		const shiny = (data.shiny ? '-shiny' : '');
		const resize = (data.h ? `background-size:${data.h}px` : '');
		const url = data.url ?? `${Dex.resourcePrefix}${data.spriteDir}${shiny}/${data.spriteid}.png`;
		return `background-image:url(${url});background-position:${data.x + xOffset}px ${data.y + yOffset}px;background-repeat:no-repeat;${resize}`;
	}
	getItemIcon(item: any) {
		let itemid = '' as ID;

		if (typeof item === 'string') {
			itemid = toID(item);
		} else if (item) {
			itemid = toID(item.id || item.name);
		}

		return [
			'background-color:transparent',
			`background-image:url(${Dex.resourcePrefix}sprites/itemicons/${itemid}.png)`,
			'background-position:center',
			'background-repeat:no-repeat',
			'background-size:24px 24px',
		].join(';');
	}
	getCustomTypeIconDir(format: string, modid: string) {
		const formatid = toID(format);

		if (modid === 'rejuvenation' || formatid.includes('rejuvenation')) {
			return 'sprites/rejuvenation/types';
		}

		return '';
	}
	getTypeIcon(type: string | null, b?: boolean, modid?: string) {
		type = this.types.get(type).name;
		if (!type) type = '???';

		const isRejuvenation =
			modid?.includes('rejuvenation') ||
			this.modid.includes('rejuvenation');

		const sanitizedType = type === '???' ? 'unknown' : type;
		const spriteDir = isRejuvenation
			? 'sprites/rejuvenation/types'
			: 'sprites/types';

		return `<img src="${Dex.resourcePrefix}${spriteDir}/${sanitizedType}.png" alt="${type}" height="14" width="32" class="pixelated${b ? ' b' : ''}" />`;
	}

	getCategoryIcon(category: string | null) {
		const categoryID = toID(category);
		let sanitizedCategory = '';
		switch (categoryID) {
		case 'physical':
		case 'special':
		case 'status':
			sanitizedCategory = categoryID.charAt(0).toUpperCase() + categoryID.slice(1);
			break;
		default:
			sanitizedCategory = 'undefined';
			break;
		}
		return `<img src="${Dex.resourcePrefix}sprites/categories/${sanitizedCategory}.png" alt="${sanitizedCategory}" height="14" width="32" class="pixelated" />`;
	}

	getPokeballs() {
		if (this.pokeballs) return this.pokeballs;
		this.pokeballs = [];
		window.BattleItems ||= {};
		for (const data of Object.values(BattleItems)) {
			if (!data.isPokeball) continue;
			this.pokeballs.push(data.name);
		}
		return this.pokeballs;
	}
};

function getTeambuilderTableKey(modid: ID): ID {
	return modid === 'gen9rejuvenation' ? 'rejuvenation' as ID : modid;
}

export class ModdedDex {
	readonly gen: number;
	readonly modid: ID;
	readonly cache = {
		Moves: {} as { [k: string]: Move },
		Items: {} as { [k: string]: Item },
		Abilities: {} as { [k: string]: Ability },
		Species: {} as { [k: string]: Species },
		Types: {} as { [k: string]: Dex.Effect },
	};
	pokeballs: string[] | null = null;
	constructor(modid: ID) {
		this.modid = modid;
		let gen = parseInt(modid.charAt(3), 10);
		if (this.modid === 'champions') gen = 9;
		if ((modid !== 'champions' && !modid.startsWith('gen')) || !gen) throw new Error("Unsupported modid");
		this.gen = gen;
	}
	text: ClientDexText = {
		getLanguage: getTextLanguage,
		get: (effect: Species | Item | Ability | Move, lang = getTextLanguage()) => {
			return getTextEntry(effect, this.gen, lang);
		},
	};
	moves = {
		get: (name: string): Move => {
			let id = toID(name);
			if (window.BattleAliases && id in BattleAliases) {
				name = BattleAliases[id];
				id = toID(name);
			}
			if (this.cache.Moves.hasOwnProperty(id)) return this.cache.Moves[id];

			let data = { ...Dex.moves.get(name) };

			for (let i = Dex.gen - 1; i >= this.gen; i--) {
				const table = window.BattleTeambuilderTable[`gen${i}`];
				if (id in table.overrideMoveData) {
					Object.assign(data, table.overrideMoveData[id]);
				}
			}
			if (this.modid !== `gen${this.gen}`) {
				const table = window.BattleTeambuilderTable[getTeambuilderTableKey(this.modid)];
				if (id in table.overrideMoveData) {
					Object.assign(data, table.overrideMoveData[id]);
				}
			}
			if (this.gen <= 3 && data.category !== 'Status') {
				data.category = Dex.getGen3Category(data.type);
			}

			const move = new Move(id, name, data);
			this.cache.Moves[id] = move;
			return move;
		},
	};

	items = {
		get: (name: string): Item => {
			let id = toID(name);
			if (window.BattleAliases && id in BattleAliases) {
				name = BattleAliases[id];
				id = toID(name);
			}
			if (this.cache.Items.hasOwnProperty(id)) return this.cache.Items[id];

			let data = { ...Dex.items.get(name) };

			for (let i = Dex.gen - 1; i >= this.gen; i--) {
				const table = window.BattleTeambuilderTable[`gen${i}`];
				if (id in table.overrideItemData) {
					Object.assign(data, table.overrideItemData[id]);
				}
			}
			if (this.modid !== `gen${this.gen}`) {
				const table = window.BattleTeambuilderTable[getTeambuilderTableKey(this.modid)];
				if (id in table.overrideItemData) {
					Object.assign(data, table.overrideItemData[id]);
				}
			}

			const item = new Item(id, name, data);
			this.cache.Items[id] = item;
			return item;
		},
	};

	abilities = {
		get: (name: string): Ability => {
			let id = toID(name);
			if (window.BattleAliases && id in BattleAliases) {
				name = BattleAliases[id];
				id = toID(name);
			}
			if (this.cache.Abilities.hasOwnProperty(id)) return this.cache.Abilities[id];

			let data = { ...Dex.abilities.get(name) };

			for (let i = Dex.gen - 1; i >= this.gen; i--) {
				const table = window.BattleTeambuilderTable[`gen${i}`];
				if (id in table.overrideAbilityData) {
					Object.assign(data, table.overrideAbilityData[id]);
				}
			}
			if (this.modid !== `gen${this.gen}`) {
				const table = window.BattleTeambuilderTable[getTeambuilderTableKey(this.modid)];
				if (id in table.overrideAbilityData) {
					Object.assign(data, table.overrideAbilityData[id]);
				}
			}

			const ability = new Ability(id, name, data);
			this.cache.Abilities[id] = ability;
			return ability;
		},
	};

	species = {
		get: (name: string): Species => {
			let id = toID(name);
			const originalId = id;
			if (window.BattleAliases && id in BattleAliases) {
				name = BattleAliases[id];
				id = toID(name);
			}
			const modTable = window.BattleTeambuilderTable[getTeambuilderTableKey(this.modid)];
			const baseSpecies = Dex.species.get(originalId || name);
			if (!baseSpecies.exists || baseSpecies.id !== originalId) {
				for (const baseSpeciesId of BattleBaseSpeciesChart) {
					if (!originalId.startsWith(baseSpeciesId)) continue;
					const cosmeticFormes = modTable?.overrideSpeciesData?.[baseSpeciesId]?.cosmeticFormes;
					const formeName = cosmeticFormes?.find(forme => toID(forme) === originalId);
					if (!formeName) continue;

					const modBaseSpecies = this.species.get(baseSpeciesId);
					const species = new Species(originalId, formeName, {
						...modBaseSpecies,
						name: formeName,
						forme: formeName.slice(modBaseSpecies.name.length + 1),
						baseForme: "",
						baseSpecies: modBaseSpecies.name,
						cosmeticFormes: null,
						otherFormes: null,
					});
					this.cache.Species[originalId] = species;
					return species;
				}
			}
			const exactModData = modTable?.overrideSpeciesData?.[originalId];
			const isStandaloneModSpecies = exactModData?.id === originalId;
			id = isStandaloneModSpecies ? originalId : (baseSpecies.exists ? baseSpecies.id : originalId);
			if (this.cache.Species.hasOwnProperty(id)) return this.cache.Species[id];

			let data = { ...baseSpecies };

			for (let i = Dex.gen - 1; i >= this.gen; i--) {
				const table = window.BattleTeambuilderTable[`gen${i}`];
				if (id in table.overrideSpeciesData) {
					Object.assign(data, table.overrideSpeciesData[id]);
				}
			}
			if (this.modid !== `gen${this.gen}`) {
				const table = window.BattleTeambuilderTable[getTeambuilderTableKey(this.modid)];
				if (id in table.overrideSpeciesData) {
					Object.assign(data, table.overrideSpeciesData[id]);
				}
			}
			if (this.gen < 3 || this.modid === 'gen7letsgo') {
				data.abilities = { 0: "No Ability" };
			}

			const table = window.BattleTeambuilderTable[getTeambuilderTableKey(this.modid)];
			if (id in table.overrideTier) data.tier = table.overrideTier[id];
			if (!data.tier && id.endsWith('totem')) {
				data.tier = this.species.get(id.slice(0, -5)).tier;
			}
			if (!data.tier && data.baseSpecies && toID(data.baseSpecies) !== id) {
				data.tier = this.species.get(data.baseSpecies).tier;
			}
			if (data.gen > this.gen) data.tier = 'Illegal';
			data.nfe = data.id === 'dipplin' || !!data.evos?.some(evo => {
				const evoSpecies = this.species.get(evo);
				return !evoSpecies.isNonstandard || evoSpecies.isNonstandard === data.isNonstandard ||
					// Pokemon with Hisui evolutions
					evoSpecies.isNonstandard === "Unobtainable";
			});

			const species = new Species(id, name, data);
			this.cache.Species[id] = species;
			return species;
		},
	};

	types = {
		namesCache: null as readonly Dex.TypeName[] | null,
		names: (): readonly Dex.TypeName[] => {
			if (this.types.namesCache) return this.types.namesCache;
			const names = Dex.types.names();
			if (!names.length) return [];
			const curNames = [...names];
			// if (this.gen < 9) curNames.splice(curNames.indexOf('Stellar'), 1);
			if (this.gen < 6) curNames.splice(curNames.indexOf('Fairy'), 1);
			if (this.gen < 2) curNames.splice(curNames.indexOf('Dark'), 1);
			if (this.gen < 2) curNames.splice(curNames.indexOf('Steel'), 1);
			this.types.namesCache = curNames;
			return curNames;
		},
		get: (name: string): Dex.Type => {
			const id = toID(name);
			name = id.substr(0, 1).toUpperCase() + id.substr(1);

			if (this.cache.Types.hasOwnProperty(id)) return this.cache.Types[id];

			let data = { ...Dex.types.get(name) };

			for (let i = 7; i >= this.gen; i--) {
				const table = window.BattleTeambuilderTable[`gen${i}`];
				if (id in table.removeType) {
					data.exists = false;
					// don't bother correcting its attributes given it doesn't exist
					break;
				}
				if (id in table.overrideTypeChart) {
					data = { ...data, ...table.overrideTypeChart[id] };
				}
			}

			this.cache.Types[id] = data;
			return data;
		},
	};
	getPokemonIcon(pokemon: string | Pokemon | ServerPokemon | Dex.PokemonSet | null, facingLeft?: boolean): string {
		return Dex.getPokemonIcon(pokemon, facingLeft, this);
	}
	getTypeIcon(type: string | null, b?: boolean) : string{
		return Dex.getTypeIcon(type, b, this.modid);
	}

	getPokeballs() {
		if (this.pokeballs) return this.pokeballs;
		this.pokeballs = [];
		window.BattleItems ||= {};
		for (const data of Object.values(BattleItems)) {
			if (data.gen && data.gen > this.gen) continue;
			if (!data.isPokeball) continue;
			this.pokeballs.push(data.name);
		}
		return this.pokeballs;
	}
}

if (typeof require === 'function') {
	// in Node
	global.Dex = Dex;
	global.toID = toID;
}
